import { useNavigate, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import FrameOverlay from "@/components/base/FrameOverlay";
import { useInView } from "@/hooks/useInView";

interface PinnedItem {
  src: string;
  label: string;
  top: number;
  left: number;
  width: number;
  rotate: string;
  z: number;
}

const initialItems: PinnedItem[] = [
  {
    src: "https://readdy.ai/api/search-image?query=Distinguished%20male%20tattoo%20artist%20with%20grey%20beard%20fifties%20wearing%20dark%20linen%20apron%20in%20luxury%20studio%20terracotta%20walls%20classical%20Roman%20column%20renaissance%20atmosphere%20warm%20museum%20lighting%20editorial%20portrait%20photography%20contemplative&width=600&height=750&seq=ap1-001&orientation=portrait",
    label: "Portrait · Juan",
    top: 4,
    left: 6,
    width: 22,
    rotate: "-4deg",
    z: 2,
  },
  {
    src: "https://readdy.ai/api/search-image?query=Large%20back%20piece%20tattoo%20with%20renaissance%20baroque%20classical%20painting%20motif%20Caravaggio%20lighting%20museum%20quality%20realism%20chiaroscuro%20warm%20bronze%20terracotta%20tones%20professional%20editorial%20tattoo%20photography&width=600&height=600&seq=ap1-002&orientation=squarish",
    label: "Back Piece · Baroque",
    top: 12,
    left: 32,
    width: 26,
    rotate: "3deg",
    z: 3,
  },
  {
    src: "https://readdy.ai/api/search-image?query=Classical%20portrait%20tattoo%20of%20Greek%20statue%20on%20forearm%20renaissance%20realism%20marble%20texture%20effect%20warm%20bronze%20lighting%20fine%20detail%20professional%20macro%20tattoo%20photography%20ivory%20background&width=600&height=750&seq=ap1-003&orientation=portrait",
    label: "Forearm · Statue Study",
    top: 2,
    left: 62,
    width: 20,
    rotate: "-2deg",
    z: 4,
  },
  {
    src: "https://readdy.ai/api/search-image?query=Renaissance%20angel%20motif%20tattoo%20on%20shoulder%20blade%20classical%20baroque%20style%20soft%20chiaroscuro%20sculptural%20detail%20warm%20amber%20tones%20professional%20editorial%20tattoo%20photography%20gallery%20quality&width=600&height=600&seq=ap1-004&orientation=squarish",
    label: "Shoulder · Angel",
    top: 44,
    left: 10,
    width: 24,
    rotate: "5deg",
    z: 5,
  },
  {
    src: "https://readdy.ai/api/search-image?query=Full%20sleeve%20tattoo%20with%20classical%20Roman%20motifs%20architectural%20columns%20statue%20figures%20renaissance%20style%20black%20and%20grey%20realism%20warm%20bronze%20terracotta%20tones%20professional%20editorial%20photography%20museum%20quality&width=600&height=750&seq=ap1-005&orientation=portrait",
    label: "Sleeve · Roman Arch",
    top: 48,
    left: 40,
    width: 20,
    rotate: "-3deg",
    z: 6,
  },
  {
    src: "https://readdy.ai/api/search-image?query=Chest%20piece%20tattoo%20baroque%20ornamental%20frame%20central%20classical%20figure%20renaissance%20inspired%20warm%20amber%20bronze%20tones%20professional%20editorial%20photography%20museum%20lighting%20sculptural%20quality&width=600&height=600&seq=ap1-006&orientation=squarish",
    label: "Chest · Baroque Frame",
    top: 52,
    left: 66,
    width: 22,
    rotate: "4deg",
    z: 7,
  },
];

interface DragState {
  index: number;
  offsetX: number;
  offsetY: number;
  moved: boolean;
  wallRect: DOMRect;
}

export default function ArtistProfile1() {
  const navigate = useNavigate();
  const wallRef = useRef<HTMLDivElement | null>(null);
  const [items, setItems] = useState<PinnedItem[]>(initialItems);
  const [hover, setHover] = useState<number | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [doorOpen, setDoorOpen] = useState(false);
  const topZRef = useRef<number>(20);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>, i: number) => {
    if (!wallRef.current) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const wallRect = wallRef.current.getBoundingClientRect();
    const frame = (e.currentTarget as HTMLElement).getBoundingClientRect();
    topZRef.current += 1;
    setItems((prev) =>
      prev.map((it, idx) => (idx === i ? { ...it, z: topZRef.current } : it))
    );
    setDrag({
      index: i,
      offsetX: e.clientX - frame.left,
      offsetY: e.clientY - frame.top,
      moved: false,
      wallRect,
    });
  };

  useEffect(() => {
    if (!drag) return;
    const handleMove = (e: PointerEvent) => {
      const { wallRect, offsetX, offsetY, index } = drag;
      const newLeftPx = e.clientX - wallRect.left - offsetX;
      const newTopPx = e.clientY - wallRect.top - offsetY;
      const widthPct = items[index].width;
      const widthPx = (widthPct / 100) * wallRect.width;
      const maxLeftPx = wallRect.width - widthPx * 0.4;
      const minLeftPx = -widthPx * 0.6;
      const maxTopPx = wallRect.height - 40;
      const minTopPx = -10;
      const clampedLeft = Math.max(minLeftPx, Math.min(newLeftPx, maxLeftPx));
      const clampedTop = Math.max(minTopPx, Math.min(newTopPx, maxTopPx));
      const leftPct = (clampedLeft / wallRect.width) * 100;
      const topPct = (clampedTop / wallRect.height) * 100;
      setItems((prev) =>
        prev.map((it, idx) =>
          idx === index ? { ...it, left: leftPct, top: topPct } : it
        )
      );
      if (!drag.moved) {
        setDrag({ ...drag, moved: true });
      }
    };
    const handleUp = () => {
      const wasMoved = drag.moved;
      setTimeout(() => setDrag(null), 0);
      if (wasMoved) {
        const stop = (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
          window.removeEventListener("click", stop, true);
        };
        window.addEventListener("click", stop, true);
      }
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [drag, items]);

  const handleClick = (i: number) => {
    if (drag && drag.index === i && drag.moved) return;
    // navigate via a real anchor click so the global shatter transition handles it
    const a = document.createElement("a");
    a.href = "/artist/marcus-aurelius";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const resetLayout = () => {
    setItems(initialItems);
    topZRef.current = 20;
  };

  return (
    <section className="bg-background-50 py-24 md:py-36 border-t border-foreground-950/10 overflow-hidden relative">
      {/* Hieroglyphic door overlay */}
      {doorOpen && (
        <div className="fixed inset-0 z-[100] pointer-events-none">
          {/* Left stone door */}
          <div className="absolute inset-y-0 left-0 w-1/2 animate-door-left fa-door-panel overflow-hidden">
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 opacity-70">
              {[...Array(6)].map((_, r) => (
                <div key={r} className="flex gap-6 animate-hiero-glow" style={{ animationDelay: `${r * 0.3}s` }}>
                  {[...Array(4)].map((_, c) => (
                    <span key={c} className="font-display text-4xl md:text-6xl" style={{ animationDelay: `${r * 0.3 + c * 0.1}s` , color: "#B5722E" }}>
                      {["𓂀", "𓋹", "𓆣", "𓅓"][c]}
                    </span>
                  ))}
                </div>
              ))}
            </div>
            {/* Stone texture */}
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath d=\"M30 5 Q35 15 40 10 Q45 5 50 15 Q55 25 50 35 Q45 45 35 50 Q25 55 15 50 Q5 45 10 35 Q15 25 10 15 Q5 5 15 5 Q25 5 30 15 Q35 25 45 25 Q55 25 55 35 Q55 45 45 45 Q35 45 25 35 Q15 25 5 25\" fill=\"none\" stroke=\"%23B5722E\" stroke-width=\"0.5\"/%3E%3C/svg%3E')", backgroundSize: "60px 60px" }} />
          </div>
          {/* Right stone door */}
          <div className="absolute inset-y-0 right-0 w-1/2 animate-door-right fa-door-panel overflow-hidden">
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 opacity-70">
              {[...Array(6)].map((_, r) => (
                <div key={r} className="flex gap-6 animate-hiero-glow" style={{ animationDelay: `${r * 0.3 + 0.15}s` }}>
                  {[...Array(4)].map((_, c) => (
                    <span key={c} className="font-display text-4xl md:text-6xl" style={{ animationDelay: `${r * 0.3 + c * 0.1 + 0.15}s` , color: "#B5722E" }}>
                      {["𓃭", "𓁈", "𓄿", "𓃒"][c]}
                    </span>
                  ))}
                </div>
              ))}
            </div>
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath d=\"M30 5 Q35 15 40 10 Q45 5 50 15 Q55 25 50 35 Q45 45 35 50 Q25 55 15 50 Q5 45 10 35 Q15 25 10 15 Q5 5 15 5 Q25 5 30 15 Q35 25 45 25 Q55 25 55 35 Q55 45 45 45 Q35 45 25 35 Q15 25 5 25\" fill=\"none\" stroke=\"%23B5722E\" stroke-width=\"0.5\"/%3E%3C/svg%3E')", backgroundSize: "60px 60px" }} />
          </div>
          {/* Golden glow center */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-full bg-gradient-to-b from-transparent via-[#B5722E]/70 to-transparent animate-hiero-glow" />
          </div>
        </div>
      )}

      <div className="px-6 md:px-12 max-w-7xl mx-auto">
        {/* Title row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
          <div>
            <p className="font-display text-[11px] uppercase tracking-[0.28em] text-foreground-700 mb-5">
              ( 01 ) — Classical Realism
            </p>
            <h3 className="font-display font-black text-foreground-950 leading-[0.9] tracking-[-0.04em] text-5xl md:text-6xl lg:text-7xl">
              Juan<em className="font-serif italic font-light"> Ramirez</em>
            </h3>
          </div>
          <div className="max-w-md">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed font-serif mb-6">
              Trained in Florence, Juan brings twenty years of classical training to the tattoo medium — channeling Caravaggio and Michelangelo onto living canvas. <em className="italic">Drag to rehang. Click a frame to enter.</em>
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/artist/marcus-aurelius"
                className="group inline-flex items-center gap-3 cursor-pointer whitespace-nowrap"
              >
                <span className="font-display text-xs uppercase tracking-[0.25em] text-foreground-950 border-b border-foreground-950/40 pb-1 group-hover:border-foreground-950 transition-colors">
                  Enter the Gallery
                </span>
                <span className="w-9 h-9 rounded-full border border-foreground-950/30 flex items-center justify-center text-foreground-950 transition-all duration-500 group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </Link>
              <button
                onClick={resetLayout}
                className="group inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
                aria-label="Reset the wall layout"
              >
                <span className="w-7 h-7 rounded-full border border-foreground-950/30 flex items-center justify-center text-foreground-700 group-hover:text-foreground-950 group-hover:border-foreground-950/60 transition-colors">
                  <i className="ri-restart-line text-[12px]"></i>
                </span>
                <span className="font-display text-[10px] uppercase tracking-[0.25em] text-foreground-700 group-hover:text-foreground-950 transition-colors">
                  Rehang
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Salon wall */}
        <div
          ref={wallRef}
          className="relative w-full bg-background-100 border border-foreground-950/15 rounded-md select-none touch-none"
          style={{ aspectRatio: "16 / 10" }}
          data-product-shop
        >
          <div className="absolute top-4 left-4 md:top-6 md:left-6 z-20 font-display text-[10px] uppercase tracking-[0.3em] text-foreground-700 pointer-events-none">
            Gallery I — Juan Ramirez
          </div>
          <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20 font-display text-[10px] uppercase tracking-[0.3em] text-foreground-700 pointer-events-none">
            06 / Works
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 font-serif italic text-[11px] text-foreground-700 pointer-events-none whitespace-nowrap">
            <i className="ri-drag-move-2-line mr-1.5"></i>
            drag any frame to rehang the gallery
          </div>

          {items.map((it, i) => {
            const isDragging = drag?.index === i;
            const isHover = hover === i && !drag;
            return (
              <PinnedFrame
                key={i}
                item={it}
                index={i}
                isDragging={isDragging}
                isHover={isHover}
                hasDrag={!!drag}
                hover={hover}
                onPointerDown={(e) => handlePointerDown(e, i)}
                onClick={() => handleClick(i)}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PinnedFrame({
  item,
  index,
  isDragging,
  isHover,
  hasDrag,
  hover,
  onPointerDown,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  item: PinnedItem;
  index: number;
  isDragging: boolean;
  isHover: boolean;
  hasDrag: boolean;
  hover: number | null;
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const { ref, isInView } = useInView({ threshold: 0.05, triggerOnce: true });

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`absolute block ${
        isDragging ? "cursor-grabbing" : "cursor-grab"
      } ${hasDrag ? "" : "transition-all duration-700 ease-out"} transition-all duration-700 ease-out ${
        isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{
        top: `${item.top}%`,
        left: `${item.left}%`,
        width: `${item.width}%`,
        transform: isDragging
          ? "rotate(0deg) scale(1.04)"
          : isHover
          ? "rotate(0deg) translateY(-10px) scale(1.06)"
          : `rotate(${item.rotate})`,
        zIndex: isDragging ? 999 : isHover ? 30 : item.z,
        filter:
          !hasDrag && hover !== null && hover !== index
            ? "grayscale(0.6) brightness(0.85)"
            : "none",
        opacity: isDragging ? 0.95 : undefined,
        transitionDelay: `${index * 120}ms`,
      }}
    >
      <div className={`overflow-hidden relative ${isDragging ? "ring-1 ring-accent-500/30" : ""}`}>
        <img
          src={item.src}
          alt={item.label}
          draggable={false}
          className="w-full h-auto block object-cover object-top"
        />
        <FrameOverlay />
      </div>
    </div>
  );
}